import React, { useMemo, useState } from 'react';
import { ChevronDown, Filter, Plus, RotateCcw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export interface FilterField {
    key: string;
    label: string;
    type: 'text' | 'select' | 'number' | 'date' | 'boolean';
    options?: { value: string; label: string }[];
    placeholder?: string;
    width?: string;
}

export interface FilterConfig {
    [key: string]: any;
}

interface InlineFiltersProps {
    fields: FilterField[];
    filters: FilterConfig;
    onFilterChange: (filters: FilterConfig) => void;
    onClearFilters: () => void;
    className?: string;
}

const InlineFilters: React.FC<InlineFiltersProps> = ({
    fields,
    filters,
    onFilterChange,
    onClearFilters,
    className = '',
}) => {
    // Get available fields that haven't been used yet (excluding always visible field)
    const alwaysVisibleField = useMemo(() => {
        return (
            fields.find(f => f.type === 'text' && f.key === 'search') ||
            fields.find(f => f.type === 'text')
        );
    }, [fields]);

    const availableUnusedFields = useMemo(() => {
        const usedKeys = Object.keys(filters).filter(key => {
            const value = filters[key];
            return value !== '' && value !== null && value !== undefined;
        });
        return fields.filter(
            field => !usedKeys.includes(field.key) && field.key !== alwaysVisibleField?.key
        );
    }, [fields, filters, alwaysVisibleField]);

    // Add a new filter
    const addFilter = (fieldKey: string) => {
        const field = fields.find(f => f.key === fieldKey);
        if (field) {
            // Don't set a default value - let user input the value first
            onFilterChange({
                ...filters,
                [fieldKey]: '',
            });
        }
    };

    // Update a filter value
    const updateFilter = (fieldKey: string, value: any) => {
        // If value is empty, remove the filter entirely
        if (value === '' || value === null || value === undefined) {
            const newFilters = { ...filters };
            delete newFilters[fieldKey];
            onFilterChange(newFilters);
        } else {
            onFilterChange({
                ...filters,
                [fieldKey]: value,
            });
        }
    };

    // Remove a specific filter
    const removeFilter = (fieldKey: string) => {
        const newFilters = { ...filters };
        delete newFilters[fieldKey];
        onFilterChange(newFilters);
    };

    // Render filter input based on field type
    const renderFilterInput = (field: FilterField, value: any) => {
        const options = field.options || [];
        const width = field.width || 'min-w-[130px]';

        switch (field.type) {
            case 'text':
                return (
                    <Input
                        value={value || ''}
                        onChange={e => updateFilter(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        className={`${width} h-8 text-sm`}
                    />
                );
            case 'select': {
                const selectedIds: string[] = value
                    ? String(value)
                          .split(',')
                          .map(s => s.trim())
                          .filter(Boolean)
                    : [];
                const validOptions = options.filter(
                    opt => opt.value !== '' && opt.value !== null && opt.value !== undefined
                );
                const triggerLabel =
                    selectedIds.length === 0
                        ? 'Select...'
                        : selectedIds.length === 1
                          ? (validOptions.find(o => o.value === selectedIds[0])?.label ??
                            selectedIds[0])
                          : `${field.label} (${selectedIds.length})`;
                return (
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                className={cn(width, 'h-8 justify-between text-sm font-normal')}
                            >
                                <span className="truncate">{triggerLabel}</span>
                                <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className={cn(width, 'p-0')} align="start">
                            <div className="max-h-60 overflow-y-auto p-1">
                                {validOptions.map(option => {
                                    const checked = selectedIds.includes(String(option.value));
                                    return (
                                        <label
                                            key={option.value}
                                            className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                                        >
                                            <Checkbox
                                                checked={checked}
                                                onCheckedChange={() => {
                                                    const next = checked
                                                        ? selectedIds.filter(
                                                              id => id !== String(option.value)
                                                          )
                                                        : [...selectedIds, String(option.value)];
                                                    updateFilter(
                                                        field.key,
                                                        next.length ? next.join(',') : ''
                                                    );
                                                }}
                                            />
                                            <span className="truncate">{option.label}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </PopoverContent>
                    </Popover>
                );
            }
            case 'number':
                return (
                    <Input
                        type="number"
                        value={value || ''}
                        onChange={e =>
                            updateFilter(
                                field.key,
                                e.target.value ? Number(e.target.value) : undefined
                            )
                        }
                        placeholder={field.placeholder}
                        className={`${width} h-8 text-sm`}
                    />
                );
            case 'date':
                return (
                    <Input
                        type="date"
                        value={value || ''}
                        onChange={e => updateFilter(field.key, e.target.value)}
                        className={`${width} h-8 text-sm`}
                    />
                );
            case 'boolean':
                return (
                    <Select
                        value={value || ''}
                        onValueChange={val => updateFilter(field.key, val === 'true')}
                    >
                        <SelectTrigger className={`${width} h-8 text-sm`}>
                            <SelectValue placeholder={`Select...`} />
                        </SelectTrigger>
                        <SelectContent>
                            {options
                                .filter(
                                    option =>
                                        option.value !== '' &&
                                        option.value !== null &&
                                        option.value !== undefined
                                )
                                .map(option => (
                                    <SelectItem key={option.value} value={String(option.value)}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                        </SelectContent>
                    </Select>
                );
            default:
                return null;
        }
    };

    return (
        <div className={`space-y-3 ${className}`}>
            {/* Filter Controls Row */}
            <div className="flex items-end gap-3 flex-wrap">
                {/* Always visible search field */}
                {alwaysVisibleField && (
                    <div className="flex flex-col gap-1 relative">
                        <label className="text-xs font-medium text-muted-foreground">
                            {alwaysVisibleField.label}
                        </label>
                        <div className="flex items-center gap-1">
                            {renderFilterInput(
                                alwaysVisibleField,
                                filters[alwaysVisibleField.key] || ''
                            )}
                            {filters[alwaysVisibleField.key] && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 hover:bg-destructive hover:text-destructive-foreground"
                                    onClick={() => removeFilter(alwaysVisibleField.key)}
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                )}

                {/* Active Filter Inputs with Labels on Top (excluding the always visible field) */}
                {Object.entries(filters)
                    .filter(([key]) => key !== alwaysVisibleField?.key)
                    .map(([key, value]) => {
                        const field = fields.find(f => f.key === key);
                        if (!field) return null;

                        return (
                            <div key={key} className="flex flex-col gap-1 relative">
                                <label className="text-xs font-medium text-muted-foreground">
                                    {field.label}
                                </label>
                                <div className="flex items-center gap-1">
                                    {renderFilterInput(field, value)}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 hover:bg-destructive hover:text-destructive-foreground"
                                        onClick={() => removeFilter(key)}
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        );
                    })}

                {/* Add Filter Dropdown */}
                <div className="flex flex-col gap-1">
                    <div className="text-xs h-4"></div>
                    {/* Spacer to align with other filters */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 flex items-center gap-2"
                            >
                                <Plus className="h-4 w-4" />
                                Add Filter
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="start"
                            className="w-56 max-h-80 overflow-y-auto"
                        >
                            <DropdownMenuLabel>Select Filter Field</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {availableUnusedFields.map(field => (
                                <DropdownMenuItem
                                    key={field.key}
                                    onClick={() => addFilter(field.key)}
                                    className="flex items-center gap-2"
                                >
                                    <Filter className="h-4 w-4" />
                                    {field.label}
                                </DropdownMenuItem>
                            ))}
                            {availableUnusedFields.length === 0 && (
                                <DropdownMenuItem disabled>
                                    <span className="text-muted-foreground">
                                        All filters are already applied
                                    </span>
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                {/* Clear All Button - After Add Filter */}
                {Object.keys(filters).length > 0 && (
                    <div className="flex flex-col gap-1">
                        <div className="text-xs h-4"></div>{' '}
                        {/* Spacer to align with other filters */}
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onClearFilters}
                            className="h-8 px-3 text-sm"
                        >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Clear All
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InlineFilters;
